import React from "react";
import { useNavigate } from "react-router-dom";
import { StatusDot } from "../components/Bits";
import {
  DASHBOARD_STATS,
  DASHBOARD_ACTIVITY,
  TONE_COLOR,
  TICKETS,
  REQUESTS,
  SEVERITY_META,
  STAGE_META,
} from "../data/mockData";

/**
 * Dashboard overview.
 *
 * Not present in the handoff design — the brief asks for it, so it is composed
 * entirely from vocabulary the design already establishes: stat cards reuse
 * `.card` + `.card-kicker`, the activity feed reuses the table's fading row
 * rule, and the two queues reuse the severity/stage dot treatment. No new
 * visual idiom is introduced. Figures derive from the same mock arrays the
 * other sections render, so the overview can never contradict them.
 */
const Dashboard = () => {
  const navigate = useNavigate();

  const triageTickets = TICKETS
    .filter((t) => t.status !== "resolved")
    .sort((a, b) => {
      const order = { critical: 0, high: 1, medium: 2, low: 3 };
      return order[a.severity] - order[b.severity];
    })
    .slice(0, 4);

  const lockedRequests = REQUESTS
    .filter((r) => r.milestones.some((m) => m.state === "locked"))
    .slice(0, 3);

  return (
    <>
      <div className="admin-stat-grid">
        {DASHBOARD_STATS.map((s) => (
          <div className="card elev-sm" key={s.id}>
            <div className="card-kicker">{s.kicker}</div>
            <div className="admin-stat-value">{s.value}</div>
            <div style={{ fontSize: 13 }}>{s.label}</div>
            <div className="card-meta">{s.meta}</div>
            <div className="admin-stat-rule" style={{ background: TONE_COLOR[s.tone] }} />
          </div>
        ))}
      </div>

      <div className="admin-split">
        <div className="card">
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <h5 style={{ margin: 0 }}>Recent activity</h5>
            <div className="admin-spacer" />
            <button type="button" className="btn btn-ghost" onClick={() => navigate("/admin/tickets")}>
              Go to tickets
            </button>
          </div>

          <div className="hr" style={{ margin: "4px 0 0" }} />

          <div className="admin-activity">
            {DASHBOARD_ACTIVITY.map((a) => (
              <div className="admin-activity-row" key={a.id}>
                <span className="admin-activity-dot" style={{ background: TONE_COLOR[a.tone] }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13 }}>{a.text}</div>
                  <div className="card-meta">{a.meta}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div className="card">
            <div className="card-kicker">Needs attention</div>
            <div className="card-title" style={{ fontSize: 15, marginBottom: 4 }}>Ticket queue</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {triageTickets.map((t) => (
                <div
                  key={t.id}
                  className="admin-row-link"
                  onClick={() => navigate(`/admin/tickets/${t.id}`)}
                  style={{ display: "flex", alignItems: "center", gap: 10 }}
                >
                  <span
                    className="admin-status-dot"
                    style={{ background: SEVERITY_META[t.severity].color }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {t.subject}
                    </div>
                    <div className="card-meta">{t.id} · {t.created}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="card">
            <div className="card-kicker">Blocked</div>
            <div className="card-title" style={{ fontSize: 15, marginBottom: 4 }}>Locked milestones</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {lockedRequests.map((r) => {
                const locked = r.milestones.find((m) => m.state === "locked");
                return (
                  <div
                    key={r.id}
                    className="admin-row-link"
                    onClick={() => navigate("/admin/requests")}
                  >
                    <div style={{ fontSize: 13 }}>{r.title}</div>
                    <div className="card-meta" style={{ color: "var(--status-warn)" }}>
                      {locked.name} — {locked.lockReason}
                    </div>
                    <StatusDot
                      color={STAGE_META[r.stage].color}
                      label={STAGE_META[r.stage].label}
                      style={{ marginTop: 4 }}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Dashboard;
